import { Schema, NodeSpec, MarkSpec, DOMOutputSpec, Node, Attrs, Fragment, Mark, NodeType } from "prosemirror-model";

import { mentionNode, tagNode } from '@/components/simple-editor/plugins/mentions/nodes';

const pDOM: DOMOutputSpec = ["p", 0], blockquoteDOM: DOMOutputSpec = ["blockquote", 0],
	hrDOM: DOMOutputSpec = ["hr"], preDOM: DOMOutputSpec = ["pre", ["code", 0]],
	brDOM: DOMOutputSpec = ["br"];


export const nodes = {
	doc: {
		content: "block+"
	} as NodeSpec,

	block: {
		content: "sub_block",
		attrs: {
			uuid: { default: null }
		},
		parseDOM: [{
			tag: "div[data-block]",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute("data-block")
				};
			}
		}],
		toDOM(node: Node) {
			return ["div", { "data-block": node.attrs.uuid }, 0];
		}
	} as NodeSpec,

	paragraph: {
		content: "inline*",
		group: "sub_block",
		parseDOM: [{ tag: "p" }],
		toDOM() { return pDOM; }
	} as NodeSpec,

	/// A blockquote (`<blockquote>`) wrapping one or more blocks.
	blockquote: {
		content: "sub_block+",
		group: "sub_block",
		defining: true,
		parseDOM: [{ tag: "blockquote" }],
		toDOM() { return blockquoteDOM; }
	} as NodeSpec,

	callout: {
		content: "inline*",
		group: "sub_block",
		defining: true,
		attrs: {
			emoji: {
				default: "🗒️"
			}
		},
		parseDOM: [{
			tag: "div[data-callout]",
			getAttrs(dom: HTMLElement) {
				return {
					emoji: dom.getAttribute('data-emoji') || '🗒️'
				};
			}
		}],
		toDOM(node: Node) { return [
			"div", {
				"data-callout": "1",
				"data-emoji": node.attrs.emoji
			},
			["div", { class: "emoji" }, node.attrs.emoji],
			["div", { class: "content" }, 0]
		];}
	},

	/// A horizontal rule (`<hr>`).
	horizontal_rule: {
		group: "sub_block",
		parseDOM: [{ tag: "hr" }],
		toDOM() { return hrDOM; }
	} as NodeSpec,

	page_separator: {
		group: "sub_block",
		attrs: {
			uuid: {
				default: null
			},
			name: {
				default: null
			},
			buttonBack: {
				default: "Back"
			},
			buttonNext: {
				default: "Next"
			},
			buttonSubmit: {
				default: "Submit"
			},
			index: {
				default: -1
			},
			isLast: {
				default: false
			},
			pageType: {
				default: 0
			}
		},
		inline: false,
		selectable: false,
		hardDeleteOnly: true,
		allowGapCursor: false,
		noCopy: true,
		parseDOM: [{
			tag: "div[data-page]",
			getAttrs(dom: HTMLElement) {
				const dataIndex = dom.getAttribute("data-index");
				const buttonBack = dom.getAttribute("data-button-back");
				const buttonNext = dom.getAttribute("data-button-next");
				const buttonSubmit = dom.getAttribute("data-button-submit");

				return {
					uuid: dom.getAttribute("data-page"),
					name: dom.getAttribute("data-name"),
					buttonBack: (buttonBack)? buttonBack : "Back",
					buttonNext: (buttonNext)? buttonNext : "Next",
					buttonSubmit: (buttonSubmit)? buttonSubmit : "Submit",
					isLast: dom.getAttribute("data-is-last") === "1",
					index: (dataIndex)? parseInt(dataIndex) : -1,
					pageType: dom.getAttribute("data-page-type")
				};
			}
		}],
		toDOM(node: Node) {
			return ["div", {
				"data-page": node.attrs.uuid,
				"data-name": node.attrs.name,
				"data-index": node.attrs.index,
				"data-button-back": node.attrs.buttonBack,
				"data-button-next": node.attrs.buttonNext,
				"data-button-submit": node.attrs.buttonSubmit,
				"data-is-last": (node.attrs.isLast)? "1" : "0",
				"data-page-type": node.attrs.pageType
			}];
		}
	} as NodeSpec,

	/// A heading textblock, with a `level` attribute that
	/// should hold the number 1 to 3. Parsed and serialized as `<h1>` to
	/// `<h6>` elements.
	heading: {
		attrs: { level: { default: 1 } },
		content: "inline*",
		group: "sub_block",
		defining: true,
		parseDOM: [{ tag: "h1", attrs: { level: 1 } },
		{ tag: "h2", attrs: { level: 2 } },
		{ tag: "h3", attrs: { level: 3 } },],
		toDOM(node) { return ["h" + node.attrs.level, 0] }
	} as NodeSpec,

	/// A code listing. Disallows marks or non-text inline
	/// nodes by default. Represented as a `<pre>` element with a
	/// `<code>` element inside of it.
	code_block: {
		content: "text*",
		marks: "",
		group: "sub_block",
		code: true,
		defining: true,
		noSelectMenu: true,
		parseDOM: [{ tag: "pre", preserveWhitespace: "full" }],
		toDOM() { return preDOM }
	} as NodeSpec,

	/// The text node.
	text: {
		group: "inline"
	} as NodeSpec,

	/// An inline image (`<img>`) node. Supports `src`,
	/// `alt`, and `href` attributes. The latter two default to the empty
	/// string.
	image: {
		group: 'sub_block',
		leaf: true,
		attrs: {
			src: {},
			size: { default: 'o' },
			position: { default: 's' },
			alt: { default: null },
			title: { default: null },
			fileUuid: { default: null }
		},
		parseDOM: [{
			tag: "img[src]", getAttrs(dom: HTMLElement) {
				return {
					src: dom.getAttribute("src"),
					title: dom.getAttribute("title"),
					position: dom.getAttribute("data-position"),
					alt: dom.getAttribute("alt"),
					size: dom.getAttribute("data-size"),
					fileUuid: dom.getAttribute("data-file-uuid")
				}
			}
		}],
		toDOM(node) {
			const { src, alt, title } = node.attrs;
			return ["img", { src, alt, title, "data-file-uuid": node.attrs.fileUuid, "data-size": node.attrs.size, "data-position": node.attrs.position }];
		}
	} as NodeSpec,

	/// A hard line break, represented in the DOM as `<br>`.
	hard_break: {
		inline: true,
		group: "inline",
		selectable: false,
		parseDOM: [{ tag: "br" }],
		toDOM() { return brDOM }
	} as NodeSpec,

	ordered_list: {
		content: 'list_item+',
		group: 'sub_block list',
		attrs: {
			order: {
				default: 1
			}
		},
		parseDOM: [{tag: 'ol'}],
		toDOM(node: Node) {
			if (node.attrs.order && node.attrs.order == 1) {
				return ["ol", 0];
			}

			return ["ol", {"start": node.attrs.order}, 0];
		}
	} as NodeSpec,

	bullet_list: {
		content: 'list_item+',
		group: 'sub_block list',
		parseDOM: [{tag: 'ul'}],
		toDOM() { return ['ul', 0]; }
	} as NodeSpec,

	list_item: {
		attrs: {
			indent: {
				default: 0
			}
		},
		content: 'paragraph list?',
		parseDOM: [{
			tag: 'li'
		}],
		defining: true,
		toDOM(node: Node) { return ['li', 0]; }
	} as NodeSpec,

	mention: mentionNode,
	tag: tagNode,

	question_placeholder: {
		group: "sub_block",
		attrs: {
			uuid: {
				default: null
			}
		},
		inline: false,
		atom: true,
		toDOM: (node: Node) => ["div", {
			"data-question-ph-uuid": node.attrs.uuid
		}],
		parseDOM: [{
			tag: "div[data-question-ph-uuid]",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-question-ph-uuid')
				};
			}
		}]
	},

	question_container: {
		group: "sub_block",
		content: "label question",
		allowGapCursor: false,
		attrs: {
			blockType: { default: 'question' },
			uuid: { default: null },
			questionType: { default: null }
		},
		parseDOM: [{
			tag: "div[data-block-type='question']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid'),
					questionType: dom.getAttribute('data-question-type')
				};
			}
		}],
		toDOM(node: Node) {
			return ["div", {
				"data-block-type": "question",
				"data-uuid": node.attrs.uuid,
				"data-question-type": node.attrs.questionType,
			}, 0];
		}
	},

	label: {
		content: "text*",
		group: "sub_block",
		attrs: {
			'for': { default: null }
		},
		parseDOM: [{
			tag: "label",
			getAttrs(dom: HTMLElement) {
				return {
					for: dom.getAttribute('for')
				};
			}
		}],
		toDOM(node) { return ["label", {for: node.attrs.for, "class": "question-label"}, 0]; }
	} as NodeSpec,

	short_text_response: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "0", // SHORT_TEXT_RESPONSE
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='0']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	text_response: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "1", // TEXT_RESPONSE
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='1']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	single_choice: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		allowGapCursor: false,
		//atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "2", // SINGLE_CHOICE
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='2']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	multiple_choice: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		allowGapCursor: false,
		//atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "3", // MULTIPLE_CHOICE
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='3']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	nps: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "4", // NPS
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='4']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	rating: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "5", // Rating
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='5']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	star_rating: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "6", // Star rating
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='6']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	dropdown: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		allowGapCursor: false,
		//atom: true,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "7", // DROPDOWN
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='7']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	matrix: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		allowGapCursor: false,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "8", // MATRIX,
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='8']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	ranking: {
		group: "question",
		attrs: {
			uuid: { default: null }
		},
		inline: false,
		allowGapCursor: false,
		selectable: false,
		noCopy: true,
		toDOM: (node: Node) => [
			"div", {
				"data-question-type": "9", // RANKING
				"data-uuid": node.attrs.uuid
			}
		],
		parseDOM: [{
			tag: "div[data-question-type='9']",
			getAttrs(dom: HTMLElement) {
				return {
					uuid: dom.getAttribute('data-uuid')
				};
			}
		}]
	} as NodeSpec,

	// DEPRECATED!!
	page_navigation: {
		content: "nav_button+",
		group: "sub_block",
		hardDeleteOnly: false,
		editorNode: true,
		noSelectMenu: true,
		noCopy: true,
		attrs: {
			last: { default: false }
		},
		parseDOM: [{
			tag: "div[data-navigation]",
			getAttrs(dom: HTMLElement) {
				return {
					last: !!(dom.getAttribute('data-for-submit') === '1')
				};
			}
		}],
		toDOM: (node: Node) => [
			"div", {
				"data-navigation": "1",
				"spellcheck": "false",
				"data-for-submit": (node.attrs.last)? '1' : null
			}, 0
		]
	} as NodeSpec,

	nav_button: {
		content: "text*",
		//inline: true,
		//selectable: false,
		//isolating: true,
		hardDeleteOnly: true,
		editorNode: true,
		noSelectMenu: true,
		noCopy: true,
		attrs: {
			role: { default: null }
		},
		parseDOM: [{
			tag: "button",
			getAttrs(dom: HTMLElement) {
				return {
					"role": dom.getAttribute('data-role')
				}
			}
		}],
		toDOM: (node: Node) => [
			"button", {
				"data-role": node.attrs.role
			}, 0
		]
	},

	video: {
		group: "sub_block",
		attrs: {
			src: {},
			poster: { default: null }
		},
		draggable: true,
		parseDOM: [{
			tag: "video", getAttrs(dom: HTMLElement) {
				const source = dom.querySelector('source');
				return {
					src: source!.getAttribute('src'),
					poster: dom.getAttribute('poster')
				};
			}
		}],
		toDOM(node: Node) {
			const { src, poster } = node.attrs;
			return ["video", { controls: '', draggable: 'false', poster}, ["source", { src }]];
		}
	},

	iframeVideo: {
		group: "sub_block",
		attrs: {
			src: {}
		},
		draggable: true,
		parseDOM: [{
			tag: "iframe[ct='media']", getAttrs(dom: HTMLElement) {
				return {
					src: dom.getAttribute('src')
				};
			}
		}],
		toDOM(node: Node) {
			const { src } = node.attrs;
			return ["iframe", {
				ct: 'media',
				width:"560",
				height:"315",
				frameborder:"0",
				allowfullscreen:"1",
				src
			}];
		}
	},
};

const emDOM: DOMOutputSpec = ["em", 0],
	strongDOM: DOMOutputSpec = ["strong", 0],
	codeDOM: DOMOutputSpec = ["code", 0],
	underlineDOM: DOMOutputSpec = ["u", 0];

/// [Specs](#model.MarkSpec) for the marks in the schema.
export const marks = {
	/// A link. Has `href` and `title` attributes. `title`
	/// defaults to the empty string. Rendered and parsed as an `<a>`
	/// element.
	link: {
		attrs: {
			href: {},
			title: { default: null }
		},
		inclusive: false,
		parseDOM: [{
			tag: "a[href]", getAttrs(dom: HTMLElement) {
				return { href: dom.getAttribute("href"), title: dom.getAttribute("title") }
			}
		}],
		toDOM(node) {
			let { href, title } = node.attrs;
			return ["a", {
				href,
				title,
				target: '_blank'
			}, 0];
		}
	} as MarkSpec,

	/// An emphasis mark. Rendered as an `<em>` element. Has parse rules
	/// that also match `<i>` and `font-style: italic`.
	em: {
		parseDOM: [
			{ tag: "i" }, { tag: "em" },
			{ style: "font-style=italic" },
			{ style: "font-style=normal", clearMark: m => m.type.name == "em" }
		],
		toDOM() { return emDOM }
	} as MarkSpec,

	/// A strong mark. Rendered as `<strong>`, parse rules also match
	/// `<b>` and `font-weight: bold`.
	strong: {
		parseDOM: [
			{ tag: "strong" },
			// This works around a Google Docs misbehavior where
			// pasted content will be inexplicably wrapped in `<b>`
			// tags with a font-weight normal.
			{ tag: "b", getAttrs: (node: HTMLElement) => node.style.fontWeight != "normal" && null },
			{ style: "font-weight=400", clearMark: m => m.type.name == "strong" },
			{ style: "font-weight", getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null },
		],
		toDOM() { return strongDOM }
	} as MarkSpec,

	underline: {
		parseDOM: [
			{ tag: "u" },
			{ style: "text-decoration=underline" }
		],
		toDOM() { return underlineDOM }
	} as MarkSpec,

	/// Code font mark. Represented as a `<code>` element.
	code: {
		parseDOM: [{ tag: "code" }],
		toDOM() { return codeDOM }
	} as MarkSpec
};

// @ts-ignore
export const schema = new Schema({ nodes, marks });
